<template>
  <UiModal
    v-if="data"
    v-model="isOpen"
    :title="data.name"
    :width="1820"
    hide-footer
  >
    <div v-if="data.id" ref="mediaDetailRef" class="w-full flex flex-col lg:flex-row gap-6 lg:gap-8 min-w-0">
      <div class="relative grow max-w-full min-w-0">
        <div
          v-if="autoPlayNext && countDown > 0"
          class="w-full md:w-auto absolute top-4 left-1/2 transform -translate-x-1/2 bg-sky-300 text-sky-900 text-center text-sm px-4 py-2 z-10 rounded shadow-lg shadow-black/20"
        >
          Reproduzindo próximo conteúdo em <strong>{{ countDown }}</strong>s
          <UiButton
            type="info"
            class="ml-4 px-2 py-1"
            @click="dismissCountdown"
          >{{ $t('button.cancel') }}</UiButton>
        </div>

        <div :key="formatSource" class="dark:bg-neutral-800 dark:text-neutral-100 rounded mb-8">
          <UiPlantarPlayer
            v-if="usePlyr"
            ref="playerRef"
            :source="formatSource"
            :type="data.type as string"
            :midiaId="data.id"
            :videoId="data.videoId"
            :trackProgress="ls.activeItem?.trackProgress !== false"
            :progress="data.progress"
            :captions="data.captions"
            :chapters="data.chapters"
            :disableFastForward="data.blockFastForward"
            :pullzone="data.pullzone"
            class="w-full aspect-video max-h-[calc(100vh-40px)]"
            @onComplete="onComplete"
            @onProgress="onProgress"
          ></UiPlantarPlayer>
          <iframe
            v-else-if="(data.type === 'link' && data.source.includes('soundcloud'))"
            :src="formatSource"
            allowfullscreen
            allowtransparency
            scrolling="no"
            franeborder="no"
            allow="autoplay"
            class="w-full aspect-video max-h-[calc(100vh-40px)]"
          />
          <UiPDFViewer
            v-else-if="data.type === 'pdf'"
            ref="pdfViewerRef"
            :source="data.source"
            :allow-download="data.allowDownload"
            :watermark="!!data.watermark"
            :watermark-rules="data.watermark"
          />
          <iframe
            v-else-if="data.type === 'link' && isEmbed"
            :src="formatSource"
            class="border-0 h-[calc(100vh-132px)] w-full"
            @load="() => onLoadFrame()"
          />
          <ExternalLink
            v-else-if="data.type === 'link' && !isEmbed"
            :url="formatSource"
          />
          <iframe
            v-else-if="data.type === 'document'"
            id="player-frame"
            :src="formatScormOrDocument"
            class="border-0 h-[calc(100vh-132px)] w-full"
            @load="() => onLoadFrame()"
          ></iframe>
          <ScormElement
            v-else-if="data.type === 'scorm'"
            :mediaId="data.id"
            :src="formatScormOrDocument"
            :trackProgress="ls.activeItem?.trackProgress !== false"
            class="border-0 h-[calc(100vh-132px)] w-full"
            @onUpdateProgress="onProgress"
          />
          <div v-if="data.type === 'image'" class="p-4">
            <img
              :src="data.source"
              :alt="data.name"
              class="max-w-[1024px] mx-auto cursor-pointer hover:ring-2 ring-offset-2 transition-all rounded w-full"
            />
            <!-- @click="() => viewingImage = true" -->
            <!-- @TODO: Implementar o zoom -->
            <!-- <VueEasyLightbox
              :visible="viewingImage"
              :imgs="[source]"
              :index="0"
              @hide="() => viewingImage = false"
            /> -->
          </div>
          <UiImageCarousel
            v-else-if="data.type === 'carousel'"
            :images="data.carousel || []"
            class="max-h-[calc(100vh-132px)] max-w-full"
            @onUpdateProgress="onProgress"
          />
          <UiFreeContent
            v-else-if="data.type === 'freeContent'"
            :initialValue="data.freeContent"
            class="py-4"
            @load="onComplete"
          />
        </div>

        <div class="flex flex-col gap-6 mb-8 max-w-full min-w-0">
          <div class="space-y-6 grow max-w-full min-w-0">
            <div class="flex flex-wrap items-center gap-2 md:gap-4">
              <div class="flex flex-wrap gap-2">
                <CardTag
                  v-for="tag in tags" :key="tag.type"
                  :type="tag.type"
                  :label="tag.label"
                  no-shrink
                />
              </div>
              <!-- Share / Favorite -->

              <!-- Modo Foco / Conteúdo -->
              <UiButton
                v-if="ls && ls?.activeItem?.parentSection"
                type="primary"
                outline
                :icon="!theatherMode ? 'arrow-autofit-width' : 'layout-sidebar-right-inactive'"
                v-tooltip="{ placement: 'bottom' }"
                :title="theatherMode ? 'Modo Foco' : 'Modo Conteúdo'"
                class="!h-9 !w-9 md:!w-auto !p-0 md:!px-2.5 flex items-center justify-center shrink-0"
                @click="theatherMode = !theatherMode"
              >
                <span class="hidden md:inline">{{ theatherMode ? 'M. Foco' : 'M. Conteudo' }}</span>
              </UiButton>

              <!-- Compartilhar -->
              <UiButton
                v-if="isSupported"
                icon="share"
                outline
                v-tooltip="{ placement: 'bottom' }"
                :title="$t('button.share')"
                class="!h-9 !w-9 md:!w-auto !p-0 md:!px-2.5 flex items-center justify-center shrink-0"
                @click="() => startShare()"
              >
                <span class="hidden md:inline">{{ $t('button.share') }}</span>
              </UiButton>

              <!-- Reação -->
              <ReactButton
                v-if="route.name !== 'files'"
                btnClass="h-9 w-9 shrink-0"
                typeContent="midia"
                :contentId="data.id"
                :reaction="data.reaction"
                :qtd="data.totalReactions"
                @onSuccess="onReacted"
              />

              <!-- Autoplay -->
              <label
                v-if="usePlyr"
                class="font-normal text-xs flex items-center h-9 px-1 shrink-0 cursor-pointer"
                v-tooltip="{ placement: 'bottom' }"
                :title="`${$t('labels.autoPlay')} - ${$t(autoPlayNext ? 'feedback.on' : 'feedback.off')}`"
              >
                <UiSwitch v-model="autoPlayNext" use-icons size="small" />
              </label>
            </div>

            <!-- Competências -->
            <div v-if="data.competencies.length > 0" class="flex flex-col max-w-full min-w-0">
              <div class="mb-4 font-semibold text-lg sm:text-xl text-neutral-950 dark:text-neutral-100">{{ $t('input.competency', 2) }}</div>
              <div class="flex gap-2 flex-wrap max-w-full">
                <div
                  v-for="cmpt in data.competencies"
                  :key="cmpt"
                  class="bg-[--c-primary-300] text-[--c-primary-800] text-sm px-2 py-1.5 rounded-md font-semibold break-words max-w-full"
                >{{ cmpt }}</div>
              </div>
            </div>

            <!-- Descrição -->
            <div v-if="data.description" class="break-words max-w-full min-w-0">
              <div class="mb-4 font-semibold text-lg sm:text-xl text-neutral-950 dark:text-neutral-100">{{ $t('column.description') }}</div>
              <div class="text-sm font-normal text-neutral-800 dark:text-neutral-300 break-words">
                {{ data.description }}
              </div>
            </div>
          </div>
        </div>

        <!-- Conteúdos Relacionados -->
        <div id="similar-content"></div>
      </div>

      <!--
        Coluna lateral (modo conteúdo): competições acima dos relacionados.
        No modo foco os dois descem para o #similar-content, abaixo da mídia.
      -->
      <Teleport to="#similar-content" defer :disabled="!theatherMode && !isMobile">
        <div v-if="hasAsideContent" class="shrink-0 space-y-8 max-w-full" :class="{ 'w-full lg:w-[380px]': !theatherMode && !isMobile, 'w-full': theatherMode || isMobile }">
          <CompetitionPoints :competitions="data.competitions" :class="{ 'max-w-[420px]': theatherMode || isMobile }" />

          <RelatedContent
            v-if="ls?.activeItem?.parentSection"
            ref="relatedContentRef"
            :section="ls.activeItem.parentSection"
            :type="['media']"
            :orientation="!theatherMode && !isMobile ? 'vertical' : 'horizontal'"
            :style="{ maxWidth: contentWidth }"
          />
        </div>
      </Teleport>
    </div>
    <div v-else>
      <UiAlert
        type="info"
        :title="$t('feedback.attention')"
        :text="$t('feedback.resourceUnavailable')"
      />
    </div>
  </UiModal>

  <RewardToast v-if="data.id" ref="rewardToast" :points="rewardPoints" />
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { breakpointsTailwind, useBreakpoints, useElementBounding, useLocalStorage, useSessionStorage, useShare } from '@vueuse/core'
import { useLearningStore } from '@/stores/learningStore'
import { i18N, learnUrl, mediaType, validateLink, competitionPoints } from '@/common/util'
import { mediaType2Api, mediaTypes } from '@/services/midias.service'
import { useRoute } from 'vue-router'

import UiAlert from '@/components/ui/alert.vue'
import UiButton from '@/components/ui/button.vue'
import UiFreeContent from '@/components/ui/freeContent/index.vue'
import UiImageCarousel from '@/components/ui/imageCarousel/index.vue'
import UiModal from '@/components/ui/modal.vue'
import UiPDFViewer from '@/components/ui/pdfViewer/index.vue'
import UiPlantarPlayer from '@/components/ui/player/plantarPlayer.vue'
import UiSwitch from '@/components/ui/switch.vue'

import ReactButton from '@/views/learningCenter/classRoom/components/reactButton.vue'
import { RewardToast } from '@/components/gamificationV2/components/reward'
import ExternalLink from './mediaDetailExternalLink.vue'
import RelatedContent from './relatedContent.vue'
import CardTag from './cardTag.vue'
import CompetitionPoints from './competitionPoints.vue'
import ScormElement from './scormElement.vue'

const isOpen = defineModel<boolean>()
const theatherMode = ref(false)
const autoPlayNext = useSessionStorage('autoPlayNext', true)
const pdfViewerRef = ref()
const playerRef = ref<InstanceType<typeof UiPlantarPlayer> | null>(null)
const relatedContentRef = ref<InstanceType<typeof RelatedContent> | null>(null)

const ls = useLearningStore()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('lg')

const normalizeType = (type: string) => {
  return mediaTypes[mediaType2Api.indexOf(type)] || 'other'
}

const recentReact = ref({
  reacted: false,
  react: '',
  count: 0
})

const data = computed(() => {
  if (!ls.activeItem?.data || typeof ls.activeItem.data !== 'object') return {}

  const dt = ls.activeItem.data
  const type = normalizeType(dt.tipo?.toLowerCase())
  let source : string = dt.link

  const opt : Record<string, any> = {}

  if (type === 'video') {
    source = `//${dt.pullzone}/${dt.videoId}/playlist.m3u8`
  } else if (type === 'scorm') {
    opt.scormType = dt.tipoScorm?.toLowerCase() || null

    // Extrai os parâmetros da URL
    const url = new URL((dt.scormScriptLink || '')?.replace(/plantarScormPlayer|plantarScormRisePlayer/g, 'lmsPlayer'))
    source = url.toString()
  }

  return {
    id: dt.id,
    videoId: dt.videoId,
    pullzone: dt.pullzone,
    type,
    name: dt.titulo,
    description: dt.descricao,
    competencies: dt.competenciasNomes,
    progress: dt.porcentagemDeConclusao || 0,
    blockFastForward: dt.bloquearAvanco || false,
    allowDownload: dt.permitirDownload || false,
    source,
    mandatory: dt.obrigatorio || false,
    opt,
    watermark: dt?.pdfMarcadAguaConfig && dt.pdfMarcadAguaConfig.includeWaterMarkInPDF ? dt.pdfMarcadAguaConfig.user : null,
    carousel: dt.carousel || [],
    captions: dt.captions || [],
    chapters: dt.chapters || [],
    freeContent: dt.conteudoLivre || '',
    reaction: recentReact.value.reacted ? recentReact.value.react : (dt.reacao || ''),
    totalReactions: recentReact.value.reacted ? recentReact.value.count : (dt.totalReacoes || 0),
    competitions: dt.competitions || []
  }
})

const rewardToast = ref()
const rewardPoints = ref(0)
const celebratedMediaId = ref('')

const competitionsPendingPoints = computed(() =>
  (data.value.competitions || [])
    .flatMap(c => c.points || [])
    .filter(p => !p.alreadyEarned)
    .reduce((acc, p) => acc + (Number(p.value) || 0), 0)
)

// Coluna lateral: sem competições e sem relacionados ela nem é renderizada, para não sobrar um vão à direita
const hasAsideContent = computed(() =>
  competitionPoints(data.value.competitions).length > 0 || !!ls.activeItem?.parentSection
)

const mediaDetailRef = ref<HTMLElement | null>(null)
const { width } = useElementBounding(mediaDetailRef)

const contentWidth = computed(() => {
  if (!mediaDetailRef.value) return '0px'

  return `${width.value}px`
})

const tags = computed(() => {
  const t : { type: string, label: string }[] = []
  if (!data.value)
    return t

  if (data.value.type) {
    const type = (data.value.type === 'media' ? data.value.type : data.value.type)
    t.push({
      type,
      label: mediaType.value[type?.toLowerCase()]
    })
  }

  if (data.value.mandatory) {
    t.push({
      type: 'required',
      label: i18N('column.required')
    })
  }

  const top10 = ls.isTop10(data.value.id, 'media')
  if (top10) {
    t.push({
      type: 'top10',
      label: `#${top10}`
    })
  }

  if (ls.isReleases(data.value.id, 'media')) {
    t.push({
      type: 'new',
      label: i18N('labels.release')
    })
  }

  return t
})

const usePlyr = computed(() => {
  if (!data.value) return false

  if (['audio', 'video'].includes(data.value.type || ''))
    return true

  if (data.value.type === 'link' && validateLink.youtube(data.value.source || '') || validateLink.vimeo(data.value.source || ''))
    return true

  return false
})

const formatScormOrDocument = computed(() => {
  if (!data.value) return ''

  const { type, source } = data.value 
  if (!source) return ''

  if (type === 'document' && ['xls', 'xlsx', 'pptx', 'ppt', 'doc', 'docx', 'odp', 'ods', 'odt'].includes(source.split('.').at(-1) || '')) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${source}`
  }

  return source
})

const isEmbed = computed(() => data.value?.source && data.value.source.includes('pembed=true'))

const formatSource = computed(() => {
  if (!data.value) return ''

  if (data.value.type === 'link' && validateLink.vimeo(data.value.source)) {
    const videoId = data.value.source.split('/').at(-1)
    return `https://player.vimeo.com/video/${videoId}`
  }

  if (data.value.type === 'link' && data.value.source.includes('pembed=true')) {
    const url = new URL(data.value.source)
    url.searchParams.delete('pembed')

    return url.toString()
  }

  if (data.value.type === 'link' && validateLink.soundcloud(data.value.source)) {
    return `https://w.soundcloud.com/player/?url=${data.value.source}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`
  }

  return data.value.source || ''
})

const onLoadFrame = () => {
}

const route = useRoute()

const SyncProgress = (progress?: number) => {
  if (!data.value) return

  return ls.updateCardProgress(data.value.id, 'media', progress || 0, ls.activeItem?.journeyId).then(() => {
    if (progress === 100
      && competitionsPendingPoints.value > 0
      && celebratedMediaId.value !== data.value.id) {
      celebratedMediaId.value = data.value.id
      rewardPoints.value = competitionsPendingPoints.value
      nextTick(() => rewardToast.value?.play())
    }

    if (route.name === 'learningPath.myJourney') {
      const refreshFlag = useLocalStorage('journeyRefreshFlag', false)
      refreshFlag.value = false
      nextTick(() => {
        refreshFlag.value = true
      })
    }

    return progress
  })
}

const onProgress = async (progress?: number) => {
  if (!progress || data.value.progress === 100) {
    return
  }

  return SyncProgress(progress)
}

const onComplete = () => {
  if (!data.value || data.value.progress === 100 || !relatedContentRef.value) return

  if (autoPlayNext.value && relatedContentRef.value?.qtdItems() > 1 && (data.value.type === 'audio' || data.value.type === 'video')) {
    setTimeout(() => {
      startCountdown()
    }, 1000)
  }

  return SyncProgress(100)
}

const countDown = ref(0)
let lastPlayedId = ''
let intervalId: ReturnType<typeof setInterval> | null = null

const onCountdownEnd = () => {
  relatedContentRef.value?.playNext()
}

const startCountdown = () => {
  if (intervalId || !data.value || lastPlayedId === data.value.id) return

  countDown.value = 5
  lastPlayedId = data.value.id

  intervalId = setInterval(() => {
    if (countDown.value > 0) {
      countDown.value--
    } else if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
      onCountdownEnd()
    }
  }, 1000)
}

const dismissCountdown = () => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
    countDown.value = 0
  }
}

const { share, isSupported } = useShare()

const startShare = () => {
  share({
    title: data.value.name,
    text: `Venha conferir "${data.value.name}"!\n\nAcesse o conteúdo em: ${location.origin}/catalog?view=${learnUrl.encode('media', data.value.id)}`,
    url: `${location.origin}/catalog?view=${learnUrl.encode('media', data.value.id)}`
  })
}

const onReacted = (res: any) => {
  ls.reactToViewingItem(res as any)

  recentReact.value.reacted = true  
  recentReact.value.react = res.reacao
  recentReact.value.count = res.totalReacoes
}

watch(
  () => data.value.id,
  (n, o) => {
    if (n !== o)
      dismissCountdown()
  }
)

// Responsável por atualizar o progresso do PDF
watch(
  () => [pdfViewerRef.value?.page, pdfViewerRef.value?.pageCount],
  (nVal, oVal) => {
    if (!oVal[1] && !!nVal[1] && nVal[1] > 1 && data.value.progress > 0 && data.value.progress < 100) {
      pdfViewerRef.value?.jumpToPercent(data.value.progress)
    }

    if (pdfViewerRef.value?.pageCount) {
      const percent = Math.round((pdfViewerRef.value.page / pdfViewerRef.value.pageCount) * 100)
      percent > data.value.progress && onProgress(percent)
    }
  }
)

// Reponsável por resetar o progresso de Imagem e Documentos e scorm
watch(() => data.value.source, (newVal) => {
  if (newVal && (data.value.type === 'image' || data.value.type === 'document' || data.value.type === 'link') && data.value.progress < 100) {
    // if (!data.value.source.includes('soundcloud')) {
      onComplete()
    // }
  }

  if (newVal && data.value.type === 'scorm' && data.value.opt?.scormType === 'htmlpackage' && data.value.progress < 100) {
    onComplete()
  }
}, { immediate: true })
</script>